import { isSodimacStandalone, isTottusStandalone } from '@digital-retail/store-manager';
import { cx } from 'cvb';
import PropTypes from 'prop-types';
import { constants } from '../../../config/constants';
import { pumApplicableRegion } from '../../../config/tenant/tenant.config';
import { withApplicationContext } from '../../../utils/ApplicationContext';
import {
  ALLOWED_CONFIGS_FOR_PRICE_BREAK,
  ALLOWED_CONFIGS_FOR_UNIT_PRICES,
  formatPriceRange,
} from '../../../utils/PriceHelpers';
import { withUser } from '../../../utils/UserContext';
import { DiscountBadge } from '../../Badges/DiscountBadge/DiscountBadge';
import { BodyCopy } from '../../ui/BodyCopy/BodyCopy';
import {
  newPriceStylesConfig,
  newUnitPriceStyleConfig,
  oldPriceStyles,
  priceSizes,
  priceStylesConfig,
  unitPriceStylesConfig,
} from '../PriceStyleConfig';
import { pricesStyles } from '../Prices.style';

const Price = ({
  priceList,
  productId,
  page,
  isPumAvailable,
  pumQuantity,
  pumUnit,
  variant,
  labels,
  isConnectProduct,
  operators,
  selectedOperatorId,
  appCtx,
  discountBadge,
  priceCount,
  product,
  userSegments,
  isUserLoggedIn,

  symbol,
  price,
  icons,
  label,
  type,
  crossed,
  unitPrice,
  i,
}) => {
  const { deviceType, store, regionCode = 'cl' } = appCtx;
  const shouldBreakFirstPrice = ALLOWED_CONFIGS_FOR_PRICE_BREAK.includes(variant);
  const pricesCount = (priceList || []).length;
  const isNormalPriceCrossedOut = appCtx?.siteConfig?.toggles?.isNormalPriceCrossedOut ?? false;
  const isNewPriceApplicable = appCtx?.siteConfig?.toggles?.isNewPriceApplicable ?? false;
  const isTottusSA = isTottusStandalone(store);
  const isSodimacSA = isSodimacStandalone(store);
  const showUnitPrice = !!ALLOWED_CONFIGS_FOR_UNIT_PRICES.includes(variant);
  const showUnitPriceXLP = showUnitPrice;
  const cmrLogoOnTop =
    page === constants.PAGES.PDP ||
    page === constants.PAGES.SLP ||
    page === constants.PAGES.PLP ||
    page === constants.PAGES.PLANS ||
    ([page, variant].includes(constants.PAGES.CART) && isNewPriceApplicable);

  let classCMRContainer = 'cmr-icon-container';
  const hasTwoPricesPlanPage = !!(page === constants.PAGES.PLANS && priceList.length === 2);
  const storeAndRegionCodeClassname = `${store || ''} ${regionCode}`;
  const cesSegment = appCtx?.siteConfig?.configurations?.CES_SEGMENT ?? {};
  const userCESSegment = cesSegment[userSegments?.find((segment) => cesSegment[segment.name])?.name];

  const getPriceImportance = (icon, _crossed) => {
    if (icon === 'cmr-icon') {
      return 'high';
    }

    if (_crossed) {
      return isNewPriceApplicable ? 'medium' : 'low';
    }

    return isNewPriceApplicable ? 'medium' : '';
  };

  const getPriceConfig = (config, icon, _crossed = false, isSep = false, hasUnitStyles = false, pricePosition = 0) => {
    const priceConfig = hasUnitStyles ? newUnitPriceStyleConfig : newPriceStylesConfig;

    const variantPriceConfig = priceConfig[variant];
    const lineHeight = variantPriceConfig?.lineHeight?.[pricePosition] ?? '';
    const lineHeightCrossed = variantPriceConfig?.lineHeight?.[2] ?? '';
    if (isNormalPriceCrossedOut) {
      return {
        ...config,
        importance: isSep && icon ? '' : getPriceImportance(icon, _crossed),
        size: isNewPriceApplicable && _crossed ? variantPriceConfig.crossed : config.size,
        lineHeight: isNewPriceApplicable && !_crossed ? lineHeight : lineHeightCrossed,
      };
    }

    return isNewPriceApplicable
      ? {
          ...config,
          importance: isSep && icon ? '' : getPriceImportance(icon, _crossed),
          lineHeight,
        }
      : config;
  };

  const getUnitPrice = (_unitPrice, _symbol) => {
    if (!_unitPrice || !showUnitPrice) {
      return '';
    }
    const { price: _price, unit } = _unitPrice;
    return _price.map((p) => `${_symbol} ${p} ${unit}`).join(' - ');
  };

  const getUnitForSale = (_unitPrice) => {
    if (!_unitPrice || !showUnitPrice) {
      return '';
    }
    const { unit } = _unitPrice;
    return unit && typeof unit === 'string' ? unit.toLowerCase() : '';
  };

  const getPrice = (_price, _symbol) => {
    return _price.map((p) => `${_symbol} ${p}`).join(' - ');
  };

  const getPerProductPrice = (_price) => {
    const cleanPrice = Number(`${_price}`.replace(/[,.]/g, ''));
    const decimalSeparator = constants.DECIMAL_SEPARATOR[regionCode];
    const dotSeparator = constants.DOT_SEPARATOR[regionCode];

    const pumPrice = `${(cleanPrice / pumQuantity).toFixed(2)}`;

    const [num, dec] = pumPrice.split('.');

    return [num.replace(/\B(?=(\d{3})+(?!\d))/g, dotSeparator), dec].join(decimalSeparator);
  };

  const isPumValid = pumApplicableRegion[regionCode] && page === constants.PAGES.PDP && isPumAvailable;

  const getPriceAttributes = (_price, _type) => {
    const priceType = _type && _type.replace('Price', '');
    return { [`data-${priceType}-price`]: _price };
  };

  const getPromotionPrice = (promotions, segment) => {
    return promotions?.length && segment !== undefined
      ? promotions.find((promotion) => promotion.campaignName === segment)
      : null;
  };

  const connectOperatorName = () => {
    if (!isConnectProduct || !selectedOperatorId) return null;
    const operator = operators.find((op) => op.id === selectedOperatorId);
    if (operator)
      return (
        <p className="operator-name">
          ({operator.name})<style jsx>{pricesStyles}</style>
        </p>
      );
    return null;
  };

  const getPriceStyle = (hasUnitStyles = false) => {
    if (isNewPriceApplicable) {
      if (hasUnitStyles) {
        return newUnitPriceStyleConfig[variant][priceList.length && priceList[0].icons ? 'cmr' : 'noCmr'];
      }
      return newPriceStylesConfig[variant][pricesCount && priceList[0].icons ? 'cmr' : 'noCmr'];
    }

    if (isNormalPriceCrossedOut) {
      if (hasUnitStyles) {
        return unitPriceStylesConfig[variant][priceList.length && priceList[0].icons ? 'cmr' : 'noCmr'];
      }
      return priceStylesConfig[variant][pricesCount && priceList[0].icons ? 'cmr' : 'noCmr'];
    }

    return oldPriceStyles[variant];
  };
  const cesPrice = getPromotionPrice(product?.promotions, userCESSegment);
  const filteredPrices = product?.prices?.filter((_price) => _price.type !== constants.PRICE_TYPE.CMR) || [];

  const lowestPrice = filteredPrices?.length
    ? filteredPrices.reduce((min, _price) => (parseFloat(_price.price[0]) > parseFloat(min.price[0]) ? min : _price))
    : null;

  const cesPriceIsLower = parseFloat(cesPrice?.prices[0]?.price[0]) < parseFloat(lowestPrice?.price[0]);
  const showCesPrice =
    cesPrice && isUserLoggedIn && (isSodimacSA || store === constants.STORES.sodimac) && cesPriceIsLower;
  const priceConfig = (hasUnitStyles) => getPriceStyle(hasUnitStyles);
  const measurement = product?.measurements?.unit;

  const isCmr = icons.length > 0 && icons === 'cmr-icon';
  const unitPriceValue = getUnitPrice(unitPrice, symbol);
  const unitForSale = getUnitForSale(unitPrice);
  const hasUnitStyles = !!showUnitPrice && !!unitPriceValue;
  classCMRContainer =
    hasTwoPricesPlanPage && isCmr ? `${classCMRContainer} cmr-icon-container-two-prices` : classCMRContainer;
  const isFirstPrice = i === 0;
  const isFirstPriceFormatNeeded = isFirstPrice && shouldBreakFirstPrice && price.length > 1;
  const priceToRender = showUnitPriceXLP && unitPrice ? unitPrice.price : price;
  const pricesToRender = isFirstPriceFormatNeeded
    ? formatPriceRange(variant, priceToRender, pricesCount, isCmr, symbol.trim())
    : [`${getPrice(priceToRender, symbol)} ${!label ? '' : label} ${priceConfig(hasUnitStyles)[i].size || ''}`];

  const dBadge =
    isTottusSA && discountBadge && !isCmr
      ? {
          ...discountBadge,
          styles: {
            backgroundColor: 'var(--primary-color)',
            textColor: 'var(--secondary-color)',
          },
        }
      : discountBadge;
  return (
    <li
      {...getPriceAttributes(price, type)}
      key={priceToRender}
      className={cx(
        `prices-${i}`,
        isNewPriceApplicable && deviceType === constants.DEVICE_MOBILE && variant === constants.PAGES.CART && 'mb-2px'
      )}
    >
      <div className={classCMRContainer} data-variant={variant}>
        {cmrLogoOnTop && icons.length > 0 && icons === 'cmr-icon' && (
          <i
            className={cx(
              `unica-cmr-icon pdp-icon ${storeAndRegionCodeClassname}`,
              isNewPriceApplicable &&
                deviceType === constants.DEVICE_MOBILE &&
                variant === constants.PAGES.CART &&
                'cmr-icon-small',
              priceCount && priceCount === 1 && 'pdp-connect-tab-plan'
            )}
          />
        )}
        {pricesToRender.map((p, priceToRenderIndex) => (
          <BodyCopy
            key={`${productId}-${p}`}
            {...getPriceConfig(priceSizes[priceConfig(hasUnitStyles)][i], icons, crossed, false, hasUnitStyles, i)}
            crossed={crossed}
            width={isFirstPriceFormatNeeded && pricesToRender.length > 1 && priceToRenderIndex === 0 ? 'width-100' : ''}
            hideCrossed={showCesPrice && crossed}
          >
            {p}
            {hasUnitStyles && <>&nbsp;{unitForSale}</>}
          </BodyCopy>
        ))}
        {measurement && (
          <BodyCopy
            {...getPriceConfig(priceSizes[priceConfig(hasUnitStyles)][i], icons, crossed, false, hasUnitStyles, i)}
            crossed={crossed}
          >
            {' '}
            {measurement}
          </BodyCopy>
        )}{' '}
        {!cmrLogoOnTop && isCmr && <i className={`unica-cmr-icon ${storeAndRegionCodeClassname}`} />}
        {isFirstPrice && isConnectProduct && (
          <div className={isCmr ? 'margin-left-6' : ''}>{connectOperatorName()}</div>
        )}
        {isFirstPrice && dBadge && <DiscountBadge badge={dBadge} />}
      </div>
      {isPumValid && (
        <div className="per-product-price">
          {`${pumUnit} ${labels.PUM_AFFIX} ${symbol} ${getPerProductPrice(priceToRender)}`}
        </div>
      )}
      <style jsx>{pricesStyles}</style>
    </li>
  );
};

Price.defaultProps = {
  productId: '',
  variantId: '',
  offeringId: '',
  isTabApplicable: false,
  page: 'POD',
  layout: '4_GRID',
  isPumAvailable: false,
  pumQuantity: 1,
  variant: 'PDP_MAIN',
  pumUnit: '',
  labels: {},
  operators: [],
  selectedOperatorId: undefined,
  isConnectProduct: false,
  appCtx: {},
  discountBadge: undefined,
  priceCount: undefined,
  userSegments: [],
  isUserLoggedIn: false,

  symbol: '',
  price: [],
  icons: '',
  label: '',
  type: '',
  crossed: false,
  unitPrice: undefined,
  pum: undefined,
  i: 0,
};

Price.propTypes = {
  priceList: PropTypes.arrayOf(PropTypes.object).isRequired,
  productId: PropTypes.string,
  variantId: PropTypes.string,
  offeringId: PropTypes.string,
  isTabApplicable: PropTypes.bool,
  page: PropTypes.oneOf(['POD', 'PDP', 'CART', 'SLP', 'PLANS']),
  layout: PropTypes.string,
  isPumAvailable: PropTypes.bool,
  pumQuantity: PropTypes.number,
  variant: PropTypes.string,
  pumUnit: PropTypes.string,
  labels: PropTypes.object,
  isConnectProduct: PropTypes.bool,
  operators: PropTypes.arrayOf(PropTypes.object),
  selectedOperatorId: PropTypes.string,
  appCtx: PropTypes.object,
  discountBadge: PropTypes.object,
  priceCount: PropTypes.number,
  product: PropTypes.object.isRequired,
  userSegments: PropTypes.array,
  isUserLoggedIn: PropTypes.bool,

  symbol: PropTypes.string,
  price: PropTypes.arrayOf(PropTypes.string),
  icons: PropTypes.string,
  label: PropTypes.string,
  type: PropTypes.string,
  crossed: PropTypes.bool,
  unitPrice: PropTypes.object,
  pum: PropTypes.any,
  i: PropTypes.number,
};

export default withApplicationContext(withUser(Price));
export { Price };
